import { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Chip,
  IconButton,
  TableSortLabel,
  Modal,
  Menu,
  MenuItem,
  TablePagination,
} from "@mui/material";
import CustomTypography from "../../common/form/CustomTypography";
import AppTableHeaderCell from "../../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../common/pages/AppTableRowCell";
import dayjs from "dayjs";
import { Task } from "../../../../models/TaskTracker/Task";
import { TaskAssignee } from "../../../../models/TaskTracker/TaskAssignee";
import { getTaskColorMap } from "../../../../models/TaskTracker/TaskColorMap";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { CustomMenu, CustomMenuItem } from "../../../../styles/Shipmentstyles";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ViewTaskPopup from "../../../pages/task-tracker/ViewTaskPopup";
import AddEditTask from "../../../pages/task-tracker/AddEditTask";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../utils/NotificationsContext";
import { status_list } from "../../../../models/TaskTracker/TaskStatus";

interface OpsSyncTaskTrackerTableProps {
  data: Task[];
  sortBy: string;
  sortType: string;
  onSortChange: (column: string) => void;
  maxHeight?: number;
  minHeight?: number;
  onTaskSuccess?: (taskData: Task) => void;
  onTaskError?: (error: any) => void;
  onStatusUpdate?: (taskId: number, newStatus: number) => void;
  total_count?: number;
  limit?: number;
  page_no?: number;
  onPageChange?: (event: any, page: number) => void;
  onRowsChange?: (rowsPerPage: number) => void;
}

const OpsSyncTaskTrackerTable = ({
  data,
  sortBy,
  sortType,
  onSortChange,
  maxHeight,
  onTaskSuccess,
  onTaskError,
  onStatusUpdate,
  minHeight = window.innerHeight - 551,
  total_count = 0,
  limit = 30,
  page_no = 1,
  onPageChange,
  onRowsChange,
}: OpsSyncTaskTrackerTableProps) => {
  const theme = useTheme();
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewPopupOpen, setViewPopupOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedTaskForStatus, setSelectedTaskForStatus] = useState<
    number | null
  >(null);
  const isOpen = Boolean(anchorEl);
  const isStatusMenuOpen = Boolean(statusMenuAnchorEl);
  const { TaskTrackerStore } = useStores();
  const { showError } = useNotification();

  const assigneeColors = useMemo(() => {
    const map: { [key: string]: string } = {};
    const taskColorMap = getTaskColorMap(theme);
    data?.forEach((taskItem) => {
      taskItem?.assignee?.forEach((assignees: TaskAssignee) => {
        const firstLetter = assignees?.name?.charAt(0)?.toUpperCase() || "";
        const key = `${assignees?.name}-${taskItem?.id}`;
        if (!map[key]) {
          map[key] =
            taskColorMap[firstLetter] ||
            theme?.palette?.customColors?.blackBodyBackgorund;
        }
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, theme]);

  const ownerColors = useMemo(() => {
    const map: { [key: string]: string } = {};
    const taskColorMap = getTaskColorMap(theme);
    data?.forEach((taskItem) => {
      if (taskItem?.owner_name) {
        const firstLetter = taskItem.owner_name.charAt(0).toUpperCase();
        const key = `${taskItem.owner_name}-${taskItem?.id}`;
        if (!map[key]) {
          map[key] =
            taskColorMap[firstLetter] ||
            theme?.palette?.customColors?.blackBodyBackgorund;
        }
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, theme]);

  const handleRequestSort = (property: string) => {
    onSortChange(property);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === "ASC" ? "↑" : "↓";
    }
    return "↑↓";
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    taskId: number
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentTaskId(taskId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentTaskId(null);
  };

  // Status chip click handler
  const handleStatusChipClick = (
    event: React.MouseEvent<HTMLElement>,
    taskId: number
  ) => {
    event.stopPropagation();
    setStatusMenuAnchorEl(event.currentTarget);
    setSelectedTaskForStatus(taskId);
  };

  // Status menu close handler
  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
    setSelectedTaskForStatus(null);
  };

  // Status option click handler
  const handleStatusOptionClick = (newStatus: number) => {
    if (selectedTaskForStatus && onStatusUpdate) {
      onStatusUpdate(selectedTaskForStatus, newStatus);
    }
    handleStatusMenuClose();
  };

  const handleEdit = () => {
    const taskId = currentTaskId;
    handleMenuClose();
    if (taskId) {
      TaskTrackerStore.getTaskDetailsById(taskId)
        .then(() => {
          setEditModalOpen(true);
        })
        .catch((error) => {
          showError(
            "Error fetching task details",
            error?.response?.data?.message || "Something went wrong!"
          );
        });
    }
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
  };

  const handleView = (taskId: number) => {
    handleMenuClose();
    TaskTrackerStore.getTaskDetailsById(taskId)
      .then(() => {
        setViewPopupOpen(true);
      })
      .catch((error) => {
        showError(
          "Error fetching task details",
          error?.response?.data?.message || "Something went wrong!"
        );
      });
  };

  const handleViewPopupClose = () => {
    setViewPopupOpen(false);
  };

  const handleRowsChange = (rowsPerPage: number) => {
    if (onRowsChange) {
      onRowsChange(rowsPerPage);
    }
  };

  return (
    <>
      {!data || data?.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxHeight: maxHeight,
            minHeight: minHeight,
            textAlign: "center",
          }}
        >
          <CustomTypography
            variant="body2"
            fontWeight={700}
            color={theme.palette.customColors.greyTextColor}
          >
            NO TASK FOUND!
          </CustomTypography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            borderRadius: "4px",
            maxHeight: maxHeight,
            minHeight: minHeight,
          }}
        >
          <Table sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor,
                  "& th": { borderBottom: "none" },
                }}
              >
                <AppTableHeaderCell
                  minWidth="80px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                >
                  ID
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  minWidth="200px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{
                    textAlign: "left",
                    padding: "0.6rem 0.6rem 0.6rem 2rem !important",
                  }}
                >
                  TASK
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  minWidth="100px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{ padding: "0.6rem !important" }}
                >
                  <TableSortLabel
                    active={sortBy === "status"}
                    direction={sortType.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleRequestSort("status")}
                    IconComponent={() => (
                      <span>{renderSortIcon("status")}</span>
                    )}
                    sx={{
                      color: theme.palette.customColors.blackBodyBackgorund,
                      "&.Mui-active": {
                        color: theme.palette.customColors.blackBodyBackgorund,
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: `${theme.palette.customColors.blackBodyBackgorund} !important`,
                      },
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    Status
                  </TableSortLabel>
                </AppTableHeaderCell>

                <AppTableHeaderCell
                  minWidth="100px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{ padding: "0.6rem !important" }}
                >
                  ASSIGNEES
                </AppTableHeaderCell>

                <AppTableHeaderCell
                  minWidth="100px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{ padding: "0.6rem !important" }}
                >
                  OWNER
                </AppTableHeaderCell>

                <AppTableHeaderCell
                  minWidth="100px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{ padding: "0.6rem !important" }}
                >
                  <TableSortLabel
                    active={sortBy === "due_date"}
                    direction={sortType.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleRequestSort("due_date")}
                    IconComponent={() => (
                      <span>{renderSortIcon("due_date")}</span>
                    )}
                    sx={{
                      color: theme.palette.customColors.blackBodyBackgorund,
                      "&.Mui-active": {
                        color: theme.palette.customColors.blackBodyBackgorund,
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: `${theme.palette.customColors.blackBodyBackgorund} !important`,
                      },
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    DUE DATE
                  </TableSortLabel>
                </AppTableHeaderCell>

                <AppTableHeaderCell
                  minWidth="100px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{ padding: "0.6rem !important" }}
                >
                  <TableSortLabel
                    active={sortBy === "created_date"}
                    direction={sortType.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleRequestSort("created_date")}
                    IconComponent={() => (
                      <span>{renderSortIcon("created_date")}</span>
                    )}
                    sx={{
                      color: theme.palette.customColors.blackBodyBackgorund,
                      "&.Mui-active": {
                        color: theme.palette.customColors.blackBodyBackgorund,
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: `${theme.palette.customColors.blackBodyBackgorund} !important`,
                      },
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    CREATED DATE
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                  sx={{ padding: "0.6rem !important" }}
                >
                  ACTION
                </AppTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((item: Task, index: number) => (
                <TableRow key={index}>
                  <AppTableRowCell
                    minWidth="80px"
                    sx={{
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => item?.id && handleView(item!.id)}
                  >
                    <CustomTypography variant="body2">
                      {item?.id}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell
                    sx={{
                      textAlign: "left",
                      width: "30vw",
                      flexWrap: "wrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      cursor: "pointer",
                    }}
                    onClick={() => item?.id && handleView(item!.id)}
                  >
                    <CustomTypography variant="body2">
                      {item?.title}
                    </CustomTypography>
                  </AppTableRowCell>

                  <AppTableRowCell
                    sx={{
                      padding: "8px",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    <Chip
                      label={
                        item?.status === 1
                          ? "TO DO"
                          : item?.status === 2
                          ? "IN PROGRESS"
                          : item?.status === 3
                          ? "BLOCKED"
                          : "COMPLETED"
                      }
                      sx={{
                        backgroundColor:
                          item?.status === 1
                            ? theme?.palette?.grey[500]
                            : item?.status === 2
                            ? theme?.palette?.info?.light
                            : item?.status === 3
                            ? theme?.palette?.error?.light
                            : theme?.palette?.success?.light,
                        color: theme?.palette?.customColors?.white,
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "25px",
                        minWidth: "100px",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.8,
                        },
                      }}
                      onClick={(event) =>
                        handleStatusChipClick(event, item.id!)
                      }
                    />
                  </AppTableRowCell>
                  <AppTableRowCell
                    sx={{
                      padding: "8px",
                      minWidth: "150px",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                        height: 35,
                        left: "42%",
                      }}
                    >
                      {item?.assignee
                        ?.slice(0, 3)
                        ?.map((assignees: TaskAssignee, idx: number) => (
                          <Chip
                            key={idx}
                            title={assignees?.name}
                            label={assignees?.name
                              ?.split(" ")
                              ?.map((namePart: string) =>
                                namePart?.charAt(0)?.toUpperCase()
                              )
                              ?.join("")}
                            sx={{
                              backgroundColor:
                                assigneeColors[
                                  `${assignees?.name}-${item?.id}`
                                ],
                              color: theme?.palette?.customColors?.white,
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              fontSize: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              position: "absolute",
                              left: `${idx * 24}px`,
                              zIndex: (item?.assignee?.length || 0) + idx,
                              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                              border: `1px solid ${theme?.palette?.customColors?.white}`,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "clip",
                              padding: 0,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                padding: 0,
                                lineHeight: "normal",
                              },
                              "&:hover": {
                                zIndex: (item?.assignee?.length || 0) + 1,
                                cursor: "default",
                              },
                            }}
                          />
                        ))}
                    </Box>
                  </AppTableRowCell>

                  <AppTableRowCell
                    sx={{
                      padding: "8px",
                      minWidth: "150px",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                        height: 35,
                      }}
                    >
                      {item?.owner_name && (
                        <Chip
                          title={item.owner_name}
                          label={item.owner_name
                            ?.split(" ")
                            .map((namePart: string) =>
                              namePart.charAt(0).toUpperCase()
                            )
                            .join("")}
                          sx={{
                            backgroundColor:
                              ownerColors[`${item?.owner_name}-${item?.id}`],
                            color: theme.palette.customColors.white,
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            fontSize: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                            border: `1px solid ${theme.palette.customColors.white}`,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "clip",
                            padding: 0,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              padding: 0,
                              lineHeight: "normal",
                            },
                            "&:hover": {
                              cursor: "default",
                            },
                          }}
                        />
                      )}
                    </Box>
                  </AppTableRowCell>

                  <AppTableRowCell>
                    <CustomTypography variant="body2">
                      {dayjs(item?.due_date).format("DD/MM/YYYY HH:mm")}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell>
                    <CustomTypography variant="body2">
                      {dayjs(item?.created_date).format("DD/MM/YYYY HH:mm")}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell
                    sx={{
                      padding: "8px",
                      fontSize: "12px",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuClick(event, item.id!)}
                    >
                      <MoreVertIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                  </AppTableRowCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {onPageChange && onRowsChange && (
            <TablePagination
              rowsPerPageOptions={[30, 50, 100, 150, 200]}
              component="div"
              count={total_count}
              rowsPerPage={limit}
              page={page_no - 1}
              onPageChange={(event, page) => onPageChange(event, page + 1)}
              onRowsPerPageChange={(event) =>
                handleRowsChange(parseInt(event.target.value, 10))
              }
            />
          )}
        </TableContainer>
      )}
      <CustomMenu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <CustomMenuItem onClick={() => handleEdit()}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "black",
            }}
          >
            <EditIcon fontSize="small" /> Edit
          </Box>
        </CustomMenuItem>
        <CustomMenuItem
          onClick={() => currentTaskId && handleView(currentTaskId)}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "black",
            }}
          >
            <VisibilityIcon fontSize="small" /> View
          </Box>
        </CustomMenuItem>
      </CustomMenu>
      <ViewTaskPopup
        open={viewPopupOpen}
        onClose={handleViewPopupClose}
        data={TaskTrackerStore.taskDetails || {}}
      />

      <Modal
        open={editModalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleEditModalClose();
          }
        }}
        aria-labelledby="edit-task-modal-title"
        aria-describedby="edit-task-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            p: 0,
            borderRadius: "4px",
            boxShadow: 24,
            width: "60vw",
            minWidth: "400px",
            maxWidth: "800px",
          }}
        >
          <IconButton
            onClick={handleEditModalClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          <AddEditTask
            isModal={true}
            onClose={handleEditModalClose}
            taskId={TaskTrackerStore.taskDetails?.id || 0}
            isNavigationNeeded={false}
            onSuccess={(taskData: Task) => {
              handleEditModalClose();
              if (onTaskSuccess) {
                onTaskSuccess(taskData);
              }
            }}
            onError={(error: any) => {
              if (onTaskError) {
                onTaskError(error);
              }
            }}
          />
        </Box>
      </Modal>

      <Menu
        anchorEl={statusMenuAnchorEl}
        open={isStatusMenuOpen}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {status_list.map((status) => (
          <MenuItem
            key={status.id}
            onClick={() => handleStatusOptionClick(Number(status.id))}
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              minWidth: "120px",
            }}
          >
            {status.value}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default OpsSyncTaskTrackerTable;
